import * as z from 'zod';

export const CreateReceiptPresetRequest = z.object({
  receiptPreset: z
    .object({
      _id: z
        .string()
        .describe('Receipt preset ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the receipt preset was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the receipt preset was last updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Receipt preset name.').max(100).optional(),
      displaySettings: z
        .object({
          businessDisplayOptions: z
            .object({
              showBusinessEmail: z
                .boolean()
                .describe(
                  'Whether to display the business email in the receipt.'
                )
                .optional(),
              showBusinessPhoneNumber: z
                .boolean()
                .describe(
                  'Whether to display the business phone number in the receipt.'
                )
                .optional(),
              showBusinessAddress: z
                .boolean()
                .describe(
                  'Whether to display the business address in the receipt.'
                )
                .optional(),
            })
            .describe('Business display options.')
            .optional(),
          customerDisplayOptions: z
            .object({
              showCustomerEmail: z
                .boolean()
                .describe(
                  "Whether to display the customer's email in the receipt."
                )
                .optional(),
              showCustomerPhoneNumber: z
                .boolean()
                .describe(
                  "Whether to display the customer's phone number in the receipt."
                )
                .optional(),
              showCustomerShippingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's shipping address in the receipt."
                )
                .optional(),
              showCustomerBillingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's billing address in the receipt."
                )
                .optional(),
              showCustomerVatId: z
                .boolean()
                .describe(
                  "Whether to display the customer's VAT ID in the receipt."
                )
                .optional(),
              showCustomerCompanyName: z
                .boolean()
                .describe("Whether to display the customer's company's name.")
                .optional(),
            })
            .describe('Customer display options.')
            .optional(),
          itemsDisplayOptions: z
            .object({
              showItemsDescription: z
                .boolean()
                .describe(
                  'Whether to display item descriptions in the receipt.'
                )
                .optional(),
              showTax: z
                .boolean()
                .describe('Whether to display item taxes in the receipt.')
                .optional(),
            })
            .describe('Item display options.')
            .optional(),
          totalsDisplayOptions: z
            .object({
              showTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the tax breakdown in the receipt.'
                )
                .optional(),
              showItemSubtotalPerTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the item subtotal per tax breakdown in the receipt.'
                )
                .optional(),
            })
            .describe('Totals display options.')
            .optional(),
        })
        .describe('Display settings.')
        .optional(),
      customFields: z
        .object({
          headerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          businessCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          customerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          footerCustomFields: z
            .array(
              z.object({
                title: z.string().describe('Title.').min(1).max(200).optional(),
                value: z
                  .string()
                  .describe('Value.')
                  .max(5000)
                  .optional()
                  .nullable(),
              })
            )
            .max(4)
            .optional(),
        })
        .describe(
          'Custom fields.\nValues appear on the receipt in the specified order.'
        )
        .optional(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
        )
        .optional(),
      displayValues: z
        .object({
          titleOverride: z
            .string()
            .describe('Text override for receipt title.')
            .min(1)
            .max(20)
            .optional()
            .nullable(),
        })
        .describe('Display values.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Receipt preset to create.'),
});
export const CreateReceiptPresetResponse = z.object({
  _id: z
    .string()
    .describe('Receipt preset ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the receipt preset was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the receipt preset was last updated.')
    .optional()
    .nullable(),
  name: z.string().describe('Receipt preset name.').max(100).optional(),
  displaySettings: z
    .object({
      businessDisplayOptions: z
        .object({
          showBusinessEmail: z
            .boolean()
            .describe('Whether to display the business email in the receipt.')
            .optional(),
          showBusinessPhoneNumber: z
            .boolean()
            .describe(
              'Whether to display the business phone number in the receipt.'
            )
            .optional(),
          showBusinessAddress: z
            .boolean()
            .describe('Whether to display the business address in the receipt.')
            .optional(),
        })
        .describe('Business display options.')
        .optional(),
      customerDisplayOptions: z
        .object({
          showCustomerEmail: z
            .boolean()
            .describe("Whether to display the customer's email in the receipt.")
            .optional(),
          showCustomerPhoneNumber: z
            .boolean()
            .describe(
              "Whether to display the customer's phone number in the receipt."
            )
            .optional(),
          showCustomerShippingAddress: z
            .boolean()
            .describe(
              "Whether to display the customer's shipping address in the receipt."
            )
            .optional(),
          showCustomerBillingAddress: z
            .boolean()
            .describe(
              "Whether to display the customer's billing address in the receipt."
            )
            .optional(),
          showCustomerVatId: z
            .boolean()
            .describe(
              "Whether to display the customer's VAT ID in the receipt."
            )
            .optional(),
          showCustomerCompanyName: z
            .boolean()
            .describe("Whether to display the customer's company's name.")
            .optional(),
        })
        .describe('Customer display options.')
        .optional(),
      itemsDisplayOptions: z
        .object({
          showItemsDescription: z
            .boolean()
            .describe('Whether to display item descriptions in the receipt.')
            .optional(),
          showTax: z
            .boolean()
            .describe('Whether to display item taxes in the receipt.')
            .optional(),
        })
        .describe('Item display options.')
        .optional(),
      totalsDisplayOptions: z
        .object({
          showTaxBreakdown: z
            .boolean()
            .describe('Whether to display the tax breakdown in the receipt.')
            .optional(),
          showItemSubtotalPerTaxBreakdown: z
            .boolean()
            .describe(
              'Whether to display the item subtotal per tax breakdown in the receipt.'
            )
            .optional(),
        })
        .describe('Totals display options.')
        .optional(),
    })
    .describe('Display settings.')
    .optional(),
  customFields: z
    .object({
      headerCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      businessCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      customerCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      footerCustomFields: z
        .array(
          z.object({
            title: z.string().describe('Title.').min(1).max(200).optional(),
            value: z
              .string()
              .describe('Value.')
              .max(5000)
              .optional()
              .nullable(),
          })
        )
        .max(4)
        .optional(),
    })
    .describe(
      'Custom fields.\nValues appear on the receipt in the specified order.'
    )
    .optional(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
    )
    .optional(),
  displayValues: z
    .object({
      titleOverride: z
        .string()
        .describe('Text override for receipt title.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
    })
    .describe('Display values.')
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const GetReceiptPresetRequest = z.object({
  receiptPresetId: z
    .string()
    .describe('Receipt preset ID to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetReceiptPresetResponse = z.object({
  _id: z
    .string()
    .describe('Receipt preset ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the receipt preset was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the receipt preset was last updated.')
    .optional()
    .nullable(),
  name: z.string().describe('Receipt preset name.').max(100).optional(),
  displaySettings: z
    .object({
      businessDisplayOptions: z
        .object({
          showBusinessEmail: z
            .boolean()
            .describe('Whether to display the business email in the receipt.')
            .optional(),
          showBusinessPhoneNumber: z
            .boolean()
            .describe(
              'Whether to display the business phone number in the receipt.'
            )
            .optional(),
          showBusinessAddress: z
            .boolean()
            .describe('Whether to display the business address in the receipt.')
            .optional(),
        })
        .describe('Business display options.')
        .optional(),
      customerDisplayOptions: z
        .object({
          showCustomerEmail: z
            .boolean()
            .describe("Whether to display the customer's email in the receipt.")
            .optional(),
          showCustomerPhoneNumber: z
            .boolean()
            .describe(
              "Whether to display the customer's phone number in the receipt."
            )
            .optional(),
          showCustomerShippingAddress: z
            .boolean()
            .describe(
              "Whether to display the customer's shipping address in the receipt."
            )
            .optional(),
          showCustomerBillingAddress: z
            .boolean()
            .describe(
              "Whether to display the customer's billing address in the receipt."
            )
            .optional(),
          showCustomerVatId: z
            .boolean()
            .describe(
              "Whether to display the customer's VAT ID in the receipt."
            )
            .optional(),
          showCustomerCompanyName: z
            .boolean()
            .describe("Whether to display the customer's company's name.")
            .optional(),
        })
        .describe('Customer display options.')
        .optional(),
      itemsDisplayOptions: z
        .object({
          showItemsDescription: z
            .boolean()
            .describe('Whether to display item descriptions in the receipt.')
            .optional(),
          showTax: z
            .boolean()
            .describe('Whether to display item taxes in the receipt.')
            .optional(),
        })
        .describe('Item display options.')
        .optional(),
      totalsDisplayOptions: z
        .object({
          showTaxBreakdown: z
            .boolean()
            .describe('Whether to display the tax breakdown in the receipt.')
            .optional(),
          showItemSubtotalPerTaxBreakdown: z
            .boolean()
            .describe(
              'Whether to display the item subtotal per tax breakdown in the receipt.'
            )
            .optional(),
        })
        .describe('Totals display options.')
        .optional(),
    })
    .describe('Display settings.')
    .optional(),
  customFields: z
    .object({
      headerCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      businessCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      customerCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      footerCustomFields: z
        .array(
          z.object({
            title: z.string().describe('Title.').min(1).max(200).optional(),
            value: z
              .string()
              .describe('Value.')
              .max(5000)
              .optional()
              .nullable(),
          })
        )
        .max(4)
        .optional(),
    })
    .describe(
      'Custom fields.\nValues appear on the receipt in the specified order.'
    )
    .optional(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
    )
    .optional(),
  displayValues: z
    .object({
      titleOverride: z
        .string()
        .describe('Text override for receipt title.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
    })
    .describe('Display values.')
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const GetDefaultReceiptPresetRequest = z.object({});
export const GetDefaultReceiptPresetResponse = z.object({
  receiptPreset: z
    .object({
      _id: z
        .string()
        .describe('Receipt preset ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the receipt preset was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the receipt preset was last updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Receipt preset name.').max(100).optional(),
      displaySettings: z
        .object({
          businessDisplayOptions: z
            .object({
              showBusinessEmail: z
                .boolean()
                .describe(
                  'Whether to display the business email in the receipt.'
                )
                .optional(),
              showBusinessPhoneNumber: z
                .boolean()
                .describe(
                  'Whether to display the business phone number in the receipt.'
                )
                .optional(),
              showBusinessAddress: z
                .boolean()
                .describe(
                  'Whether to display the business address in the receipt.'
                )
                .optional(),
            })
            .describe('Business display options.')
            .optional(),
          customerDisplayOptions: z
            .object({
              showCustomerEmail: z
                .boolean()
                .describe(
                  "Whether to display the customer's email in the receipt."
                )
                .optional(),
              showCustomerPhoneNumber: z
                .boolean()
                .describe(
                  "Whether to display the customer's phone number in the receipt."
                )
                .optional(),
              showCustomerShippingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's shipping address in the receipt."
                )
                .optional(),
              showCustomerBillingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's billing address in the receipt."
                )
                .optional(),
              showCustomerVatId: z
                .boolean()
                .describe(
                  "Whether to display the customer's VAT ID in the receipt."
                )
                .optional(),
              showCustomerCompanyName: z
                .boolean()
                .describe("Whether to display the customer's company's name.")
                .optional(),
            })
            .describe('Customer display options.')
            .optional(),
          itemsDisplayOptions: z
            .object({
              showItemsDescription: z
                .boolean()
                .describe(
                  'Whether to display item descriptions in the receipt.'
                )
                .optional(),
              showTax: z
                .boolean()
                .describe('Whether to display item taxes in the receipt.')
                .optional(),
            })
            .describe('Item display options.')
            .optional(),
          totalsDisplayOptions: z
            .object({
              showTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the tax breakdown in the receipt.'
                )
                .optional(),
              showItemSubtotalPerTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the item subtotal per tax breakdown in the receipt.'
                )
                .optional(),
            })
            .describe('Totals display options.')
            .optional(),
        })
        .describe('Display settings.')
        .optional(),
      customFields: z
        .object({
          headerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          businessCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          customerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          footerCustomFields: z
            .array(
              z.object({
                title: z.string().describe('Title.').min(1).max(200).optional(),
                value: z
                  .string()
                  .describe('Value.')
                  .max(5000)
                  .optional()
                  .nullable(),
              })
            )
            .max(4)
            .optional(),
        })
        .describe(
          'Custom fields.\nValues appear on the receipt in the specified order.'
        )
        .optional(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
        )
        .optional(),
      displayValues: z
        .object({
          titleOverride: z
            .string()
            .describe('Text override for receipt title.')
            .min(1)
            .max(20)
            .optional()
            .nullable(),
        })
        .describe('Display values.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Default receipt preset.')
    .optional(),
});
export const UpdateReceiptPresetRequest = z.object({
  _id: z
    .string()
    .describe('Receipt preset ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  receiptPreset: z
    .object({
      _id: z
        .string()
        .describe('Receipt preset ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the receipt preset was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the receipt preset was last updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Receipt preset name.').max(100).optional(),
      displaySettings: z
        .object({
          businessDisplayOptions: z
            .object({
              showBusinessEmail: z
                .boolean()
                .describe(
                  'Whether to display the business email in the receipt.'
                )
                .optional(),
              showBusinessPhoneNumber: z
                .boolean()
                .describe(
                  'Whether to display the business phone number in the receipt.'
                )
                .optional(),
              showBusinessAddress: z
                .boolean()
                .describe(
                  'Whether to display the business address in the receipt.'
                )
                .optional(),
            })
            .describe('Business display options.')
            .optional(),
          customerDisplayOptions: z
            .object({
              showCustomerEmail: z
                .boolean()
                .describe(
                  "Whether to display the customer's email in the receipt."
                )
                .optional(),
              showCustomerPhoneNumber: z
                .boolean()
                .describe(
                  "Whether to display the customer's phone number in the receipt."
                )
                .optional(),
              showCustomerShippingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's shipping address in the receipt."
                )
                .optional(),
              showCustomerBillingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's billing address in the receipt."
                )
                .optional(),
              showCustomerVatId: z
                .boolean()
                .describe(
                  "Whether to display the customer's VAT ID in the receipt."
                )
                .optional(),
              showCustomerCompanyName: z
                .boolean()
                .describe("Whether to display the customer's company's name.")
                .optional(),
            })
            .describe('Customer display options.')
            .optional(),
          itemsDisplayOptions: z
            .object({
              showItemsDescription: z
                .boolean()
                .describe(
                  'Whether to display item descriptions in the receipt.'
                )
                .optional(),
              showTax: z
                .boolean()
                .describe('Whether to display item taxes in the receipt.')
                .optional(),
            })
            .describe('Item display options.')
            .optional(),
          totalsDisplayOptions: z
            .object({
              showTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the tax breakdown in the receipt.'
                )
                .optional(),
              showItemSubtotalPerTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the item subtotal per tax breakdown in the receipt.'
                )
                .optional(),
            })
            .describe('Totals display options.')
            .optional(),
        })
        .describe('Display settings.')
        .optional(),
      customFields: z
        .object({
          headerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          businessCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          customerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          footerCustomFields: z
            .array(
              z.object({
                title: z.string().describe('Title.').min(1).max(200).optional(),
                value: z
                  .string()
                  .describe('Value.')
                  .max(5000)
                  .optional()
                  .nullable(),
              })
            )
            .max(4)
            .optional(),
        })
        .describe(
          'Custom fields.\nValues appear on the receipt in the specified order.'
        )
        .optional(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
        )
        .optional(),
      displayValues: z
        .object({
          titleOverride: z
            .string()
            .describe('Text override for receipt title.')
            .min(1)
            .max(20)
            .optional()
            .nullable(),
        })
        .describe('Display values.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Receipt preset to update. May be partial.'),
});
export const UpdateReceiptPresetResponse = z.object({
  _id: z
    .string()
    .describe('Receipt preset ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the receipt preset was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the receipt preset was last updated.')
    .optional()
    .nullable(),
  name: z.string().describe('Receipt preset name.').max(100).optional(),
  displaySettings: z
    .object({
      businessDisplayOptions: z
        .object({
          showBusinessEmail: z
            .boolean()
            .describe('Whether to display the business email in the receipt.')
            .optional(),
          showBusinessPhoneNumber: z
            .boolean()
            .describe(
              'Whether to display the business phone number in the receipt.'
            )
            .optional(),
          showBusinessAddress: z
            .boolean()
            .describe('Whether to display the business address in the receipt.')
            .optional(),
        })
        .describe('Business display options.')
        .optional(),
      customerDisplayOptions: z
        .object({
          showCustomerEmail: z
            .boolean()
            .describe("Whether to display the customer's email in the receipt.")
            .optional(),
          showCustomerPhoneNumber: z
            .boolean()
            .describe(
              "Whether to display the customer's phone number in the receipt."
            )
            .optional(),
          showCustomerShippingAddress: z
            .boolean()
            .describe(
              "Whether to display the customer's shipping address in the receipt."
            )
            .optional(),
          showCustomerBillingAddress: z
            .boolean()
            .describe(
              "Whether to display the customer's billing address in the receipt."
            )
            .optional(),
          showCustomerVatId: z
            .boolean()
            .describe(
              "Whether to display the customer's VAT ID in the receipt."
            )
            .optional(),
          showCustomerCompanyName: z
            .boolean()
            .describe("Whether to display the customer's company's name.")
            .optional(),
        })
        .describe('Customer display options.')
        .optional(),
      itemsDisplayOptions: z
        .object({
          showItemsDescription: z
            .boolean()
            .describe('Whether to display item descriptions in the receipt.')
            .optional(),
          showTax: z
            .boolean()
            .describe('Whether to display item taxes in the receipt.')
            .optional(),
        })
        .describe('Item display options.')
        .optional(),
      totalsDisplayOptions: z
        .object({
          showTaxBreakdown: z
            .boolean()
            .describe('Whether to display the tax breakdown in the receipt.')
            .optional(),
          showItemSubtotalPerTaxBreakdown: z
            .boolean()
            .describe(
              'Whether to display the item subtotal per tax breakdown in the receipt.'
            )
            .optional(),
        })
        .describe('Totals display options.')
        .optional(),
    })
    .describe('Display settings.')
    .optional(),
  customFields: z
    .object({
      headerCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      businessCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      customerCustomFields: z
        .array(
          z.object({
            title: z
              .string()
              .describe('Title.')
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            value: z.string().describe('Value.').max(100).optional(),
          })
        )
        .max(4)
        .optional(),
      footerCustomFields: z
        .array(
          z.object({
            title: z.string().describe('Title.').min(1).max(200).optional(),
            value: z
              .string()
              .describe('Value.')
              .max(5000)
              .optional()
              .nullable(),
          })
        )
        .max(4)
        .optional(),
    })
    .describe(
      'Custom fields.\nValues appear on the receipt in the specified order.'
    )
    .optional(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
    )
    .optional(),
  displayValues: z
    .object({
      titleOverride: z
        .string()
        .describe('Text override for receipt title.')
        .min(1)
        .max(20)
        .optional()
        .nullable(),
    })
    .describe('Display values.')
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const SetDefaultReceiptPresetRequest = z.object({
  receiptPresetId: z
    .string()
    .describe('Receipt preset ID to set as default.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const SetDefaultReceiptPresetResponse = z.object({
  receiptPreset: z
    .object({
      _id: z
        .string()
        .describe('Receipt preset ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the receipt preset was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the receipt preset was last updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Receipt preset name.').max(100).optional(),
      displaySettings: z
        .object({
          businessDisplayOptions: z
            .object({
              showBusinessEmail: z
                .boolean()
                .describe(
                  'Whether to display the business email in the receipt.'
                )
                .optional(),
              showBusinessPhoneNumber: z
                .boolean()
                .describe(
                  'Whether to display the business phone number in the receipt.'
                )
                .optional(),
              showBusinessAddress: z
                .boolean()
                .describe(
                  'Whether to display the business address in the receipt.'
                )
                .optional(),
            })
            .describe('Business display options.')
            .optional(),
          customerDisplayOptions: z
            .object({
              showCustomerEmail: z
                .boolean()
                .describe(
                  "Whether to display the customer's email in the receipt."
                )
                .optional(),
              showCustomerPhoneNumber: z
                .boolean()
                .describe(
                  "Whether to display the customer's phone number in the receipt."
                )
                .optional(),
              showCustomerShippingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's shipping address in the receipt."
                )
                .optional(),
              showCustomerBillingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's billing address in the receipt."
                )
                .optional(),
              showCustomerVatId: z
                .boolean()
                .describe(
                  "Whether to display the customer's VAT ID in the receipt."
                )
                .optional(),
              showCustomerCompanyName: z
                .boolean()
                .describe("Whether to display the customer's company's name.")
                .optional(),
            })
            .describe('Customer display options.')
            .optional(),
          itemsDisplayOptions: z
            .object({
              showItemsDescription: z
                .boolean()
                .describe(
                  'Whether to display item descriptions in the receipt.'
                )
                .optional(),
              showTax: z
                .boolean()
                .describe('Whether to display item taxes in the receipt.')
                .optional(),
            })
            .describe('Item display options.')
            .optional(),
          totalsDisplayOptions: z
            .object({
              showTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the tax breakdown in the receipt.'
                )
                .optional(),
              showItemSubtotalPerTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the item subtotal per tax breakdown in the receipt.'
                )
                .optional(),
            })
            .describe('Totals display options.')
            .optional(),
        })
        .describe('Display settings.')
        .optional(),
      customFields: z
        .object({
          headerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          businessCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          customerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          footerCustomFields: z
            .array(
              z.object({
                title: z.string().describe('Title.').min(1).max(200).optional(),
                value: z
                  .string()
                  .describe('Value.')
                  .max(5000)
                  .optional()
                  .nullable(),
              })
            )
            .max(4)
            .optional(),
        })
        .describe(
          'Custom fields.\nValues appear on the receipt in the specified order.'
        )
        .optional(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
        )
        .optional(),
      displayValues: z
        .object({
          titleOverride: z
            .string()
            .describe('Text override for receipt title.')
            .min(1)
            .max(20)
            .optional()
            .nullable(),
        })
        .describe('Display values.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Default receipt preset.')
    .optional(),
});
export const DeleteReceiptPresetRequest = z.object({
  receiptPresetId: z
    .string()
    .describe('Receipt preset ID to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteReceiptPresetResponse = z.object({});
export const ListReceiptPresetsRequest = z.object({});
export const ListReceiptPresetsResponse = z.object({
  receiptPresets: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Receipt preset ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the receipt preset was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the receipt preset was last updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Receipt preset name.').max(100).optional(),
        displaySettings: z
          .object({
            businessDisplayOptions: z
              .object({
                showBusinessEmail: z
                  .boolean()
                  .describe(
                    'Whether to display the business email in the receipt.'
                  )
                  .optional(),
                showBusinessPhoneNumber: z
                  .boolean()
                  .describe(
                    'Whether to display the business phone number in the receipt.'
                  )
                  .optional(),
                showBusinessAddress: z
                  .boolean()
                  .describe(
                    'Whether to display the business address in the receipt.'
                  )
                  .optional(),
              })
              .describe('Business display options.')
              .optional(),
            customerDisplayOptions: z
              .object({
                showCustomerEmail: z
                  .boolean()
                  .describe(
                    "Whether to display the customer's email in the receipt."
                  )
                  .optional(),
                showCustomerPhoneNumber: z
                  .boolean()
                  .describe(
                    "Whether to display the customer's phone number in the receipt."
                  )
                  .optional(),
                showCustomerShippingAddress: z
                  .boolean()
                  .describe(
                    "Whether to display the customer's shipping address in the receipt."
                  )
                  .optional(),
                showCustomerBillingAddress: z
                  .boolean()
                  .describe(
                    "Whether to display the customer's billing address in the receipt."
                  )
                  .optional(),
                showCustomerVatId: z
                  .boolean()
                  .describe(
                    "Whether to display the customer's VAT ID in the receipt."
                  )
                  .optional(),
                showCustomerCompanyName: z
                  .boolean()
                  .describe("Whether to display the customer's company's name.")
                  .optional(),
              })
              .describe('Customer display options.')
              .optional(),
            itemsDisplayOptions: z
              .object({
                showItemsDescription: z
                  .boolean()
                  .describe(
                    'Whether to display item descriptions in the receipt.'
                  )
                  .optional(),
                showTax: z
                  .boolean()
                  .describe('Whether to display item taxes in the receipt.')
                  .optional(),
              })
              .describe('Item display options.')
              .optional(),
            totalsDisplayOptions: z
              .object({
                showTaxBreakdown: z
                  .boolean()
                  .describe(
                    'Whether to display the tax breakdown in the receipt.'
                  )
                  .optional(),
                showItemSubtotalPerTaxBreakdown: z
                  .boolean()
                  .describe(
                    'Whether to display the item subtotal per tax breakdown in the receipt.'
                  )
                  .optional(),
              })
              .describe('Totals display options.')
              .optional(),
          })
          .describe('Display settings.')
          .optional(),
        customFields: z
          .object({
            headerCustomFields: z
              .array(
                z.object({
                  title: z
                    .string()
                    .describe('Title.')
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  value: z.string().describe('Value.').max(100).optional(),
                })
              )
              .max(4)
              .optional(),
            businessCustomFields: z
              .array(
                z.object({
                  title: z
                    .string()
                    .describe('Title.')
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  value: z.string().describe('Value.').max(100).optional(),
                })
              )
              .max(4)
              .optional(),
            customerCustomFields: z
              .array(
                z.object({
                  title: z
                    .string()
                    .describe('Title.')
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  value: z.string().describe('Value.').max(100).optional(),
                })
              )
              .max(4)
              .optional(),
            footerCustomFields: z
              .array(
                z.object({
                  title: z
                    .string()
                    .describe('Title.')
                    .min(1)
                    .max(200)
                    .optional(),
                  value: z
                    .string()
                    .describe('Value.')
                    .max(5000)
                    .optional()
                    .nullable(),
                })
              )
              .max(4)
              .optional(),
          })
          .describe(
            'Custom fields.\nValues appear on the receipt in the specified order.'
          )
          .optional(),
        default: z
          .boolean()
          .describe(
            "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
          )
          .optional(),
        displayValues: z
          .object({
            titleOverride: z
              .string()
              .describe('Text override for receipt title.')
              .min(1)
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Display values.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
      })
    )
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z
    .object({
      namespaceData: z
        .record(z.string(), z.any())
        .describe(
          'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
        ),
    })
    .describe('Field options. The `namespaceData` object **must** be passed.'),
});
export const UpdateExtendedFieldsResponse = z.object({
  receiptPreset: z
    .object({
      _id: z
        .string()
        .describe('Receipt preset ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the receipt preset is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the receipt preset.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the receipt preset was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the receipt preset was last updated.')
        .optional()
        .nullable(),
      name: z.string().describe('Receipt preset name.').max(100).optional(),
      displaySettings: z
        .object({
          businessDisplayOptions: z
            .object({
              showBusinessEmail: z
                .boolean()
                .describe(
                  'Whether to display the business email in the receipt.'
                )
                .optional(),
              showBusinessPhoneNumber: z
                .boolean()
                .describe(
                  'Whether to display the business phone number in the receipt.'
                )
                .optional(),
              showBusinessAddress: z
                .boolean()
                .describe(
                  'Whether to display the business address in the receipt.'
                )
                .optional(),
            })
            .describe('Business display options.')
            .optional(),
          customerDisplayOptions: z
            .object({
              showCustomerEmail: z
                .boolean()
                .describe(
                  "Whether to display the customer's email in the receipt."
                )
                .optional(),
              showCustomerPhoneNumber: z
                .boolean()
                .describe(
                  "Whether to display the customer's phone number in the receipt."
                )
                .optional(),
              showCustomerShippingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's shipping address in the receipt."
                )
                .optional(),
              showCustomerBillingAddress: z
                .boolean()
                .describe(
                  "Whether to display the customer's billing address in the receipt."
                )
                .optional(),
              showCustomerVatId: z
                .boolean()
                .describe(
                  "Whether to display the customer's VAT ID in the receipt."
                )
                .optional(),
              showCustomerCompanyName: z
                .boolean()
                .describe("Whether to display the customer's company's name.")
                .optional(),
            })
            .describe('Customer display options.')
            .optional(),
          itemsDisplayOptions: z
            .object({
              showItemsDescription: z
                .boolean()
                .describe(
                  'Whether to display item descriptions in the receipt.'
                )
                .optional(),
              showTax: z
                .boolean()
                .describe('Whether to display item taxes in the receipt.')
                .optional(),
            })
            .describe('Item display options.')
            .optional(),
          totalsDisplayOptions: z
            .object({
              showTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the tax breakdown in the receipt.'
                )
                .optional(),
              showItemSubtotalPerTaxBreakdown: z
                .boolean()
                .describe(
                  'Whether to display the item subtotal per tax breakdown in the receipt.'
                )
                .optional(),
            })
            .describe('Totals display options.')
            .optional(),
        })
        .describe('Display settings.')
        .optional(),
      customFields: z
        .object({
          headerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          businessCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          customerCustomFields: z
            .array(
              z.object({
                title: z
                  .string()
                  .describe('Title.')
                  .min(1)
                  .max(100)
                  .optional()
                  .nullable(),
                value: z.string().describe('Value.').max(100).optional(),
              })
            )
            .max(4)
            .optional(),
          footerCustomFields: z
            .array(
              z.object({
                title: z.string().describe('Title.').min(1).max(200).optional(),
                value: z
                  .string()
                  .describe('Value.')
                  .max(5000)
                  .optional()
                  .nullable(),
              })
            )
            .max(4)
            .optional(),
        })
        .describe(
          'Custom fields.\nValues appear on the receipt in the specified order.'
        )
        .optional(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default receipt preset.\nThe default preset is used during receipt creation when a preset isn't specified or doesn't exist."
        )
        .optional(),
      displayValues: z
        .object({
          titleOverride: z
            .string()
            .describe('Text override for receipt title.')
            .min(1)
            .max(20)
            .optional()
            .nullable(),
        })
        .describe('Display values.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the receipt preset.\n\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated receipt preset.')
    .optional(),
});
